import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {Box, Button, Checkbox, Collapse, FormControlLabel, MenuItem, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography} from '@material-ui/core';
import {API_BASE_URL} from './config';

export function isAiEmployee(auth) {
  const roles = auth?.tokenParsed?.realm_access?.roles || [];
  return auth?.authenticated && roles.some(role => ['co.rtb', 'lid.rtb_employee'].includes(role));
}

export function AiSessionsRoute({auth, keycloakClient}) {
  // App context contains parsed claims only. Refresh and bearer access must use
  // the initialized Keycloak client, without copying stale credentials into state.
  return <AiSessionsScreen auth={auth ? keycloakClient : null} />;
}
const number = value => typeof value === 'number' ? value.toLocaleString(undefined, {maximumFractionDigits: 2}) : '—';

function Details({row}) {
  const media = row.media || {};
  return <Box p={2}>
    <Typography variant="subtitle2">Backend audio metrics</Typography>
    <Typography variant="body2">Generation {row.generation} · ICE {media.iceState || '—'} · Transport {media.connectionState || '—'}</Typography>
    <Typography variant="body2">PCM input {number(media.pcmInputBytes)} B · output {number(media.pcmOutputBytes)} B</Typography>
    <Table size="small" aria-label={`Metrics for session ${row.id}`}><TableBody>
      {Object.entries(media.audioMetrics || {}).map(([key, value]) => <TableRow key={key}>
        <TableCell component="th" scope="row">{key}</TableCell><TableCell>{number(value)}</TableCell>
      </TableRow>)}
    </TableBody></Table>
    {!Object.keys(media.audioMetrics || {}).length && <Typography variant="body2">No audio measurements for this session.</Typography>}
    {(media.rtp || []).map((rtp, index) => <Typography key={index} variant="body2">
      {rtp.direction}: packets received {number(rtp.packetsReceived)}, lost {number(rtp.packetsLost)}, jitter {number(rtp.jitter)} RTP ticks
    </Typography>)}
  </Box>;
}

export default function AiSessionsScreen({auth}) {
  const permitted = isAiEmployee(auth);
  const [data, setData] = useState(null), [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false), [automatic, setAutomatic] = useState(true);
  const [refresh, setRefresh] = useState(0), [filter, setFilter] = useState('all');
  const [search, setSearch] = useState(''), [expanded, setExpanded] = useState(null);
  useEffect(() => {
    if (!permitted) { setData(null); return undefined; }
    let stopped = false, timer;
    const cancel = axios.CancelToken.source();
    async function load() {
      setRefreshing(true);
      let credentialsReady = false;
      try {
        // Fail closed on refresh failure instead of using the legacy interceptor fallback.
        await auth.updateToken(30);
        if (stopped) return;
        if (!auth.authenticated || !auth.token) throw new Error('Login expired');
        credentialsReady = true;
        const reply = await axios.get(`${API_BASE_URL}/api/v1/ai/gateway/debug/sessions`, {
          headers: {Authorization: `Bearer ${auth.token}`}, timeout: 8000, cancelToken: cancel.token
        });
        if (!stopped) { setData(reply.data); setError(''); }
      } catch (failure) {
        if (!stopped && !axios.isCancel(failure)) {
          const status = failure.response?.status;
          if (!credentialsReady || status === 401 || status === 403 || !auth.authenticated) setData(null);
          setError(status === 401 ? 'Login expired. Sign in again.' : status === 403 ?
            'RTB employee access is required.' : 'Diagnostics could not be refreshed. Retry or check the gateway.');
        }
      } finally {
        if (!stopped) { setRefreshing(false); if (automatic) timer = setTimeout(load, 5000); }
      }
    }
    load();
    return () => { stopped = true; clearTimeout(timer); cancel.cancel(); };
  }, [auth, permitted, automatic, refresh]);

  if (!permitted) return <Typography role="alert">RTB employee access is required.</Typography>;
  const current = data?.current || [], recent = data?.recent || [], server = data?.server || {};
  const rows = [...(filter !== 'recent' ? current.map(r => ({...r, source: 'Current'})) : []),
    ...(filter !== 'current' ? recent.map(r => ({...r, source: 'Recent'})) : [])]
    .filter(row => `${row.id} ${row.mode} ${row.model} ${row.state} ${row.error_code || ''}`.toLowerCase().includes(search.toLowerCase()));
  return <Box py={3}>
    <Typography variant="h4" component="h1" gutterBottom>AI Sessions</Typography>
    <Typography variant="body2">Read-only backend diagnostics. No prompts, images, audio or user identities. History resets on gateway restart.</Typography>
    <Box my={2} display="flex" flexWrap="wrap" alignItems="center" style={{gap: 16}}>
      <Button variant="contained" onClick={() => setRefresh(v => v + 1)} disabled={refreshing}>Refresh</Button>
      <FormControlLabel control={<Checkbox checked={automatic} onChange={e => setAutomatic(e.target.checked)} />} label="Auto-refresh (5 s)" />
      <TextField id="ai-session-scope" select label="Sessions" value={filter} onChange={e => setFilter(e.target.value)} style={{minWidth: 120}}>
        <MenuItem value="all">All</MenuItem><MenuItem value="current">Current</MenuItem><MenuItem value="recent">Recent</MenuItem>
      </TextField>
      <TextField id="ai-session-search" label="Filter ID, mode, model, state" value={search} onChange={e => setSearch(e.target.value)} style={{width: 280, maxWidth: '100%'}} />
    </Box>
    {error && <Box role="alert" mb={2}><Typography color="error">{error}</Typography></Box>}
    <Typography role="status" variant="body2">{refreshing ? 'Refreshing…' : data ? `Updated ${new Date(data.sampledAt * 1000).toLocaleTimeString()}` : error ? 'Unavailable' : 'Loading…'}{error && data ? ' · Showing stale data' : ''}</Typography>
    <Box my={2} component={Paper} p={2}>
      <Typography>Current {current.length} · Recent {recent.length} · Active operations {number(server.activeOperations)}</Typography>
      <Typography variant="body2">Loop lag max {number(server.loop_lag_max_ms)} ms · Python threads {number(server.python_threads)} · Native threads {number(server.native_threads)}</Typography>
      <Typography variant="body2">Client / iPhone mixer metrics are not uploaded here. Backend counters do not prove audible quality.</Typography>
    </Box>
    <TableContainer component={Paper} tabIndex={0} role="region" aria-label="Scrollable AI sessions"><Table size="small" aria-label="AI sessions" style={{minWidth: 1000}}>
      <TableHead><TableRow>{['Details', 'Session', 'Scope', 'Mode / model', 'State', 'Started', 'Duration (s)', 'Error code'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
      <TableBody>{rows.map(row => {
        const key = `${row.source}-${row.id}-${row.generation}`, open = expanded === key;
        return <React.Fragment key={key}><TableRow>
          <TableCell><Button size="small" aria-label={`${open ? 'Hide' : 'Show'} details for ${row.id}`} aria-expanded={open}
            aria-controls={`details-${key}`} onClick={() => setExpanded(open ? null : key)}>{open ? 'Hide' : 'Show'}</Button></TableCell>
          <TableCell style={{fontFamily: 'monospace'}}>{row.id}</TableCell><TableCell>{row.source}</TableCell>
          <TableCell>{row.mode}<br />{row.model}</TableCell><TableCell>{row.state}</TableCell>
          <TableCell>{new Date(row.started_at * 1000).toLocaleString()}</TableCell><TableCell>{number(row.duration_seconds)}</TableCell><TableCell>{row.error_code || '—'}</TableCell>
        </TableRow><TableRow><TableCell colSpan={8} style={{padding: 0, borderBottom: open ? undefined : 0}}>
          <Collapse in={open} unmountOnExit><div id={`details-${key}`}><Details row={row} /></div></Collapse>
        </TableCell></TableRow></React.Fragment>;
      })}</TableBody>
    </Table>{!rows.length && <Box p={3}><Typography>{data ? 'No matching sessions.' : 'Waiting for diagnostics.'}</Typography></Box>}</TableContainer>
    <Box mt={2}><Typography variant="caption">Recent history: at most {data?.retention?.maximumRecent || 200} sessions / {(data?.retention?.seconds || 3600) / 60} minutes in process memory. Only technical summaries are retained after deletion.</Typography></Box>
  </Box>;
}
